import React, {
  useState,
  useMemo,
  useEffect,
  useCallback,
  useContext,
} from "react";

import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import { SortingRule } from "react-table";
import { HvButton, HvGrid } from "@hitachivantara/uikit-react-core";
import {
  useHvTable,
  HvTableColumnConfig,
  useHvPagination,
  useHvSortBy,
  HvCellProps,
} from "@hitachivantara/uikit-react-lab";
import { Open } from "@hitachivantara/uikit-react-icons";

import moment from "moment";

import {
  textColumn,
  dateColumn,
  numberColumn,
  actionsColumn,
  badgeColumn,
  tagColumn,
  iconColumn,
  DEFAULT_PAGE,
  DEFAULT_PAGE_SIZE,
  progressColumn,
} from "components/common/SimpleTable";

import {
  useIncidents,
  IncidentType,
  useIncidentTypes,
  Severity,
  useSeverity,
  Status,
  useStatus,
  Priority,
  usePriorities,
  useTimeSettings,
} from "lib/api";

import { formatToUTC } from "lib/utils/datetime";
import { getUserDisplay } from "lib/utils/user";
import { MapContext } from "lib/context/MapContext";

import { normalizeProgressBar } from "components/common/SimpleTable/renderers/ProgressColumnCell";
import IncidentTableControls from "../IncidentsTableControls";

import Table from "./Table";

interface IncidentDataRow extends Record<string, unknown> {
  id: string;
  name: string;
  incidentType?: IncidentType;
  priority?: Priority;
  severity?: Severity;
  status?: Status;
  updatedDate: string;
  createdDate: string;
  assignee?: string;
  cameras: number;
  events: number;
}

const sortFieldMap = {
  name: "inc_name",
  incidentType: "incident_type.inc_type_name",
  priority: "fk_inc_priority_id",
  severity: "fk_inc_severity_id",
  status: "fk_inc_status_id",
  updatedDate: "update_timestamp",
  createdDate: "create_timestamp",
  assignee: "inc_assigned_user",
  events: "hv_entity_type_id",
  camera: "inc_nearest_cameras",
};

const DEFAULT_SORT = [
  {
    id: "updatedDate",
    desc: true,
  },
];

const IncidentsTable: React.FC = () => {
  const { t } = useTranslation("map");
  const [tableData, setTableData] = useState<IncidentDataRow[] | null>(null);
  const [totalCount, setTotalCount] = useState(0);
  const [searchKey, setSearchKey] = useState("");
  const [tableControlKey, setTableControlKey] = useState(Date.now());
  const history = useHistory();

  const [filter, setFilter] = useState<Record<string, unknown>>({});

  const columns = useMemo(
    (): HvTableColumnConfig<IncidentDataRow>[] => [
      dateColumn({
        Header: t("columns.incidentUpdatedDate") as string,
        accessor: "updatedDate",
        style: { width: "15%" },
      }),
      dateColumn({
        Header: t("columns.incidentCreatedDate") as string,
        accessor: "createdDate",
        style: { width: "15%" },
      }),
      textColumn({
        Header: t("columns.incidentName") as string,
        accessor: "name",
        style: { width: "20%" },
      }),
      iconColumn(
        {
          Header: t("columns.incidentType") as string,
          accessor: "incidentType",
          style: { width: "15%" },
        },
        "appearance",
        "inc_type_name",
        9
      ),
      textColumn({
        Header: t("columns.incidentAssignee") as string,
        accessor: "assignee",
        style: { width: "10%" },
      }),
      badgeColumn(
        {
          Header: t("columns.incidentPriority") as string,
          accessor: "priority",
          style: { width: "10%" },
        },
        "P",
        "inc_priority_order",
        "prt_color",
        "prt_name",
        false,
        5
      ),
      badgeColumn(
        {
          Header: t("columns.incidentSeverity") as string,
          accessor: "severity",
          style: { width: "10%" },
        },
        "S",
        "inc_severity_order",
        "svrt_color",
        "svrt_name",
        false,
        5
      ),
      tagColumn(
        {
          Header: t("columns.incidentStatus") as string,
          accessor: "status",
          style: { width: "10%" },
        },
        "status_name",
        "status_color",
        "status_text_color"
      ),
      numberColumn({
        Header: t("columns.incidentEvents") as string,
        accessor: "events",
        style: { width: 90 },
        disableSortBy: true,
      }),
      numberColumn({
        Header: t("columns.incidentCameras") || undefined,
        accessor: "cameras",
        style: { width: 100 },
        disableSortBy: true,
      }),
      numberColumn({
        Header: t("columns.incidentSOP") || undefined,
        accessor: "sopCount",
        style: { width: 75 },
        disableSortBy: true,
      }),
      progressColumn(
        {
          Header: t("columns.incidentSOPProgress") || undefined,
          accessor: "completedSteps",
          style: { width: 125 },
          disableSortBy: true,
        },
        (row) => row.original.completedSteps as number,
        (row) => row.original.totalSteps as number,
        "secondary"
      ),
      textColumn({
        accessor: "sop_steps",
        style: { width: 45, paddingLeft: 0, textAlign: "right" },
        disableSortBy: true,
        Cell: ({ row }: HvCellProps<IncidentDataRow, string>) =>
          row.original.totalSteps
            ? `${normalizeProgressBar(
                row.original.completedSteps as number,
                row.original.totalSteps as number
              )}%`
            : "",
      }),
      actionsColumn({
        id: "openIncident",
        Cell: ({ row }: HvCellProps<IncidentDataRow>) => {
          return (
            <HvButton
              aria-label={t("labels.editIncidentButtonLabel")}
              icon
              onClick={() => history.push(`/incidents/${row.original.id}`)}
            >
              <Open />
            </HvButton>
          );
        },
      }),
    ],
    [t, history]
  );

  const [page, setPage] = useState(DEFAULT_PAGE);
  const [size, setSize] = useState(DEFAULT_PAGE_SIZE);
  const [sortBy, setSortBy] =
    useState<SortingRule<IncidentDataRow>[]>(DEFAULT_SORT);

  const sortField = sortFieldMap[sortBy[0].id as keyof typeof sortFieldMap];

  const { timezone } = useTimeSettings();

  const [periodStart, setPeriodStart] = useState<Date | undefined>(undefined);
  const [periodEnd, setPeriodEnd] = useState<Date | undefined>(undefined);

  const onChangeDates = useCallback((startDate?: Date, endDate?: Date) => {
    if (startDate != null && endDate != null) {
      setPeriodStart(startDate);
      setPeriodEnd(endDate);
    } else {
      setPeriodStart(undefined);
      setPeriodEnd(undefined);
    }

    // go back to first page
    setPage(0);
  }, []);

  const { incidentIds, selectedKpi } = useContext(MapContext);

  let dateFrom;
  let dateTo;
  if (periodStart != null && periodEnd != null) {
    dateFrom = formatToUTC(
      moment(periodStart).tz(timezone, true).startOf("day"),
      timezone
    );
    dateTo = formatToUTC(
      moment(periodEnd).tz(timezone, true).endOf("day"),
      timezone
    );
  }

  const { data: incidentsData, error: incidentsError } = useIncidents(
    {
      pageNr: page + 1,
      pageSize: size,
      orderBy: encodeURIComponent(sortField),
      Order: sortBy[0].desc ? "desc" : "asc",
      dateFrom,
      dateTo,
      SearchKey: searchKey,
    },
    { suspense: tableData == null },
    filter
  );

  const { data: incidentTypesData, error: incidentTypesError } =
    useIncidentTypes(undefined, { suspense: tableData == null });

  const { data: priorityData, error: priorityError } = usePriorities({
    suspense: tableData == null,
  });

  const { data: severityData, error: severityError } = useSeverity({
    suspense: tableData == null,
  });

  const { data: statusData, error: statusError } = useStatus({
    suspense: tableData == null,
  });

  useEffect(() => {
    if (
      incidentsData &&
      incidentTypesData &&
      priorityData &&
      severityData &&
      statusData
    ) {
      setTableData(
        incidentsData.data.map((incident) => {
          const status = statusData?.data.find(
            (st) => st.pk_status_id === incident.fk_inc_status_id
          );

          return {
            id: `${incident.pk_inc_id}`,
            name: incident.inc_name,
            incidentType: incidentTypesData?.data.find(
              (incidentType) =>
                incidentType.pk_inc_type_id === incident.fk_inc_type_id
            ),
            priority: priorityData?.data.find(
              (priority) => priority.pk_prt_id === incident.fk_inc_priority_id
            ),
            severity: severityData?.data.find(
              (severity) => severity.pk_svrt_id === incident.fk_inc_severity_id
            ),
            status,
            createdDate: incident.create_timestamp,
            updatedDate: incident.update_timestamp,
            assignee: getUserDisplay(incident.inc_assigned_user),
            cameras: incident.inc_nearest_camera_ids?.length,
            events: incident.hv_correlated_entities?.length,
            sopCount: incident.inc_sop_map.length,
            completedSteps: incident.inc_overall_completed_steps,
            totalSteps: incident.inc_overall_steps,
          };
        })
      );
      setTotalCount(incidentsData?.totalCount);
    }
  }, [
    incidentsData,
    incidentTypesData,
    priorityData,
    severityData,
    statusData,
  ]);

  const initialState = useMemo(() => {
    return {
      pageIndex: page,
      pageSize: size,
      sortBy,
    };
  }, [sortBy, page, size]);

  const labels = useMemo(
    () => ({
      selectRowCheckBoxAriaLabel: t("labels.selectIncidentCheckbox"),
    }),
    [t]
  );

  const instance = useHvTable<IncidentDataRow>(
    {
      columns,
      data: tableData || [],
      getRowId: useCallback((v: IncidentDataRow): string => `${v.id}`, []),
      manualPagination: true,
      pageCount: Math.ceil((totalCount ?? 0) / (initialState?.pageSize ?? 1)),
      initialState,
      labels,
      manualSortBy: true,
      disableMultiSort: true,
      disableSortRemove: true,
      disableCreateExpandButton: true,
    },
    useHvSortBy,
    useHvPagination
  );

  const {
    state: { pageIndex, pageSize, sortBy: sortByFromTable },
  } = instance;

  useEffect(() => setPage?.(pageIndex), [pageIndex, setPage]);
  useEffect(() => setSize?.(pageSize), [pageSize, setSize]);
  useEffect(() => {
    setSortBy?.(sortByFromTable);
    setPage?.(0);
  }, [sortByFromTable, setSortBy, setPage]);
  useEffect(() => {
    setFilter({
      incidentIds: {
        filterValues: incidentIds,
      },
    });
    setSearchKey("");
    setTableControlKey(Date.now());
    if (selectedKpi !== "KPI_TODAY_INCIDENT") {
      setPeriodStart(undefined);
      setPeriodEnd(undefined);
    } else {
      const today = moment()
        .tz(timezone)
        .startOf("day")
        .utc(true)
        .local(true)
        .toDate();
      setPeriodStart(today);
      setPeriodEnd(today);
    }
  }, [selectedKpi, setSearchKey, incidentIds, timezone]);

  const onSearchHandler = (value: string) => {
    setSearchKey(value);
    instance.gotoPage(DEFAULT_PAGE);
  };

  const onFilterHandler = (value: number[][]) => {
    setFilter({
      incidentTypeFilter: {
        filterValues: value[0] || [],
      },
      priority: {
        filterValues: value[1] || [],
      },
      severity: {
        filterValues: value[2] || [],
      },
      status: {
        filterValues: value[3] || [],
      },
      assignee: {
        filterValues: value[4] || [],
      },
      incidentIds: {
        filterValues: incidentIds || [],
      },
    });
    instance.gotoPage(DEFAULT_PAGE);
  };

  return tableData != null ? (
    <HvGrid container spacing={2}>
      <HvGrid item xs={12}>
        <IncidentTableControls
          key={tableControlKey}
          onDateRangeChange={onChangeDates}
          onSearch={onSearchHandler}
          onFilter={onFilterHandler}
          currentItemsCount={totalCount}
          totalItemsCount={incidentIds?.length}
        />
      </HvGrid>
      <HvGrid item xs={12}>
        <Table
          instance={instance}
          totalCount={totalCount}
          loading={
            (!incidentsData && !incidentsError) ||
            (!incidentTypesData && !incidentTypesError) ||
            (!statusData && !statusError) ||
            (!priorityData && !priorityError) ||
            (!severityData && !severityError) ||
            tableData === []
          }
        />
      </HvGrid>
    </HvGrid>
  ) : (
    <></>
  );
};
export default IncidentsTable;
